import { useState, useEffect, useRef } from "react";
import api from "../../services/api";

import editIcon from "../../assets/edit-icon.png";
import deleteIcon from "../../assets/delete-icon.png";

const COLORS = {
  primary: "#0D47A1",
  danger: "#D32F2F",
  bg: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E0E0E0",
  text: "#1E1E1E",
  muted: "#757575",
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formRef = useRef(null);

  const loadUsers = async () => {
    try {
      const res = await api.get("/users/list.php");
      setUsers(res.data?.data || []);
    } catch {
      setError("Failed to load users");
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showForm && formRef.current && !formRef.current.contains(e.target)) {
        resetForm();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setName("");
    setUsername("");
    setPassword("");
    setError("");
  };

  const validate = () => {
    if (!name.trim()) return "Name required";
    if (!username.trim()) return "Username required";
    if (!editingId && !password.trim()) return "Password required";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: name.trim(),
        username: username.trim(),
        password: password.trim(),
      };

      if (editingId) {
        await api.post("/users/update.php", {
          id: editingId,
          ...payload,
        });
      } else {
        await api.post("/users/create.php", payload);
      }

      resetForm();
      loadUsers();
    } catch {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setName(user.name || "");
    setUsername(user.username || "");
    setPassword("");
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;

    try {
      await api.post("/users/delete.php", { id });
      loadUsers();
    } catch {
      setError("Delete failed");
    }
  };

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Users</h2>
          <p style={styles.subtitle}>Manage system users</p>
        </div>

        <button
          style={styles.addBtn}
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          + Add User
        </button>
      </div>

      {/* FORM */}
      {showForm && (
        <div ref={formRef} style={styles.formCard}>
          <form onSubmit={submit} style={styles.formGrid}>
            
            <div style={styles.field}>
              <label style={styles.label}>Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter name"
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Username</label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                style={styles.input}
              />
            </div>

            <div style={styles.fullWidth}>
              <button type="submit" style={styles.saveBtn} disabled={loading}>
                {loading
                  ? editingId
                    ? "Updating..."
                    : "Adding..."
                  : editingId
                  ? "Update"
                  : "Save"}
              </button>
            </div>
          </form>

          {error && <p style={styles.error}>{error}</p>}
        </div>
      )}

      {/* TABLE */}
      <div style={styles.card}>
        {users.length === 0 ? (
          <p style={styles.empty}>No users found</p>
        ) : (
          <div style={styles.list}>
            {users.map((u) => (
              <div key={u.id} style={styles.userCard}>
                <div style={styles.userInfo}>
                  <p style={styles.username}>{u.username}</p>
                  <p style={styles.role}>{u.name}</p>
                </div>

                <div style={styles.actions}>
                  <button onClick={() => handleEdit(u)} style={styles.iconBtn}>
                    <img src={editIcon} alt="edit" style={styles.icon} />
                  </button>

                  <button onClick={() => handleDelete(u.id)} style={styles.iconBtn}>
                    <img src={deleteIcon} alt="delete" style={styles.icon} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ================= RESPONSIVE ================= */

const styles = {
  page: {
    background: COLORS.bg,
    minHeight: "100vh",
    padding: "clamp(12px, 4vw, 30px)",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 20,
  },

  title: {
    fontSize: "clamp(18px, 2.5vw, 26px)",
    fontWeight: 700,
  },

  subtitle: {
    fontSize: 13,
    color: COLORS.muted,
  },

addBtn: {
  background: COLORS.primary,
  color: "#fff",
  padding: "10px 20px",
  borderRadius: 10,
  border: "none",
  fontWeight: 600,
  height: 42,                 // ✅ IMPORTANT
  cursor: "pointer",
  display: "flex",            // ✅ alignment fix
  alignItems: "center",
  justifyContent: "center",
},

  formCard: {
    background: "#fff",
    padding: 20,
    borderRadius: 16,
    marginBottom: 20,
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },

  field: { display: "flex", flexDirection: "column" },

  label: { fontSize: 13, marginBottom: 6 },

  input: {
    padding: 12,
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
  },

  fullWidth: { gridColumn: "1 / -1" },

  saveBtn: {
    background: COLORS.primary,
    color: "#fff",
    padding: 12,
    border: "none",
    borderRadius: 8,
    width: "100%",
  },

  card: {
    background: "#fff",
    borderRadius: 18,
    padding: 15,
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  userCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 14,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
  },

  userInfo: {
    display: "flex",
    flexDirection: "column",
  },

  username: {
    fontWeight: 600,
  },

  role: {
    fontSize: 12,
    color: COLORS.muted,
  },

  actions: {
    display: "flex",
    gap: 10,
  },

  iconBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },

  icon: {
    width: 18,
    height: 18,
  },

  empty: {
    textAlign: "center",
    padding: 20,
    color: COLORS.muted,
  },

  error: {
    color: COLORS.danger,
    marginTop: 10,
  },
};

export default Users;